import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ArrowRight, Users, MessageCircle, Shield, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { getWelcomeImagesPublic } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_HERO_IMAGES = [
  { url: 'https://images.unsplash.com/photo-1663627650051-9fdc3a53a075?w=800', caption: 'Find your one', alt: 'Happy Guyanese couple embracing outdoors' },
  { url: 'https://images.pexels.com/photos/5635639/pexels-photo-5635639.jpeg?auto=compress&w=800', caption: 'Start your journey today', alt: 'Smiling couple enjoying a sunny day together' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [heroImages, setHeroImages] = useState(DEFAULT_HERO_IMAGES);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    let cancelled = false;
    getWelcomeImagesPublic()
      .then(res => {
        if (cancelled) return;
        const imgs = (res.data || []).map(i => ({
          url: i.url?.startsWith('http') ? i.url : `${BACKEND_URL}${i.url}`,
          caption: i.caption || '',
          alt: i.alt_text || i.caption || 'Welcome picture'
        }));
        if (imgs.length > 0) setHeroImages(imgs);
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (heroImages.length <= 1) return undefined;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  // Keep index within bounds if images shrink
  useEffect(() => {
    if (currentImageIndex >= heroImages.length) setCurrentImageIndex(0);
  }, [heroImages.length, currentImageIndex]);

  const features = [
    {
      icon: Users,
      title: 'Guyanese Community',
      description: 'Connect with fellow Guyanese at home and abroad'
    },
    {
      icon: Heart,
      title: 'Find Your Match',
      description: 'Swipe through profiles and discover your perfect partner'
    },
    {
      icon: MessageCircle,
      title: 'Private Messaging',
      description: 'Chat securely with your matches in real-time'
    },
    {
      icon: Shield,
      title: 'Safe & Secure',
      description: 'Verified profiles and robust privacy controls'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 hero-gradient">
          <div className="absolute inset-0 bg-black/20"></div>
          {/* Abstract shapes */}
          <div className="absolute top-20 right-10 w-64 h-64 bg-[#FFCE00]/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#E2231A]/10 rounded-full blur-3xl"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-white"
            >
              <div className="flex items-center gap-2 mb-6">
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" fill="white" />
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>GuyanaConnect</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Outfit' }}>
                Find Love in the
                <span className="block text-[#FFCE00]">Guyanese Diaspora</span>
              </h1>

              <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-lg">
                The premier dating platform connecting Guyanese hearts worldwide. Meet authentic, verified singles from Guyana and the diaspora.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-[#FFCE00] hover:bg-[#e6b800] text-[#0a0a0a] font-semibold px-8 py-6 text-lg rounded-full"
                  onClick={() => navigate('/register')}
                  data-testid="get-started-btn"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full"
                  onClick={() => navigate('/login')}
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
              </div>

              {/* Stats */}
              <div className="flex gap-8 mt-12">
                <div>
                  <div className="text-3xl font-bold text-[#FFCE00]">10K+</div>
                  <div className="text-white/70 text-sm">Active Members</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#FFCE00]">500+</div>
                  <div className="text-white/70 text-sm">Matches Made</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#FFCE00]">4.8</div>
                  <div className="text-white/70 text-sm flex items-center gap-1">
                    <Star className="w-3 h-3" fill="currentColor" /> Rating
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right - Image Cards */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block relative"
            >
              <div className="relative w-full max-w-md mx-auto">
                {/* Main card */}
                <div className="relative w-80 h-[420px] mx-auto rounded-3xl overflow-hidden shadow-2xl" data-testid="hero-image">
                  <img
                    src={heroImages[currentImageIndex]?.url}
                    alt={heroImages[currentImageIndex]?.alt || heroImages[currentImageIndex]?.caption || 'Welcome'}
                    className="w-full h-full object-cover transition-opacity duration-500"
                    key={heroImages[currentImageIndex]?.url}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-6 left-6 text-white">
                    <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                      {heroImages[currentImageIndex]?.caption || 'Find Your One'}
                    </div>
                    <div className="text-white/80">Start your journey today</div>
                  </div>
                  {heroImages.length > 1 && (
                    <div className="absolute bottom-3 right-3 flex gap-1.5">
                      {heroImages.map((_, i) => (
                        <span
                          key={i}
                          className={`h-1.5 rounded-full transition-all ${i === currentImageIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/50'}`}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Floating elements */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#009E60]/10 flex items-center justify-center">
                      <Heart className="w-6 h-6 text-[#009E60]" fill="#009E60" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">New Match!</div>
                      <div className="text-sm text-gray-500">Just now</div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#FFCE00]/20 flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-[#FFCE00]" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Start Chatting</div>
                      <div className="text-sm text-gray-500">Connect now</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              Why GuyanaConnect?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're dedicated to helping Guyanese singles find meaningful connections
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow card-hover"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#009E60]/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-[#009E60]" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#009E60]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              Ready to Find Your Match?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Join thousands of Guyanese singles who have found love on our platform
            </p>
            <Button
              size="lg"
              className="bg-[#FFCE00] hover:bg-[#e6b800] text-[#0a0a0a] font-semibold px-10 py-6 text-lg rounded-full"
              onClick={() => navigate('/register')}
              data-testid="cta-signup-btn"
            >
              Create Free Account
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-[#009E60] flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" fill="white" />
              </div>
              <span className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>GuyanaConnect</span>
            </div>
            <div className="flex gap-6 text-gray-400">
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-contact-link">Contact</Link>
            </div>
          </div>
          <div className="text-center text-gray-500 text-sm mt-8">
            © 2024 GuyanaConnect. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
